/**
 * Real trigger API routes — calls `gi-api-server` endpoints.
 *
 * Admin endpoints (create, enable, disable) bypass JWT auth and are
 * gated solely by the `X-Founder-Signature` header. The signature is
 * computed externally by the founder and pasted into the UI's signing
 * dialog. Providing a custom fetch here instead of `apiFetch` because
 * those endpoints need `X-Founder-Signature` but NOT the regular
 * `Authorization` / `X-AXIOM-*` headers.
 */

import type {
  CreateTriggerRequest,
  CreateTriggerResponse,
  CronValidateResponse,
  FireTriggerResponse,
  SetTriggerEnabledRequest,
  SetTriggerEnabledResponse,
  TriggerFireEvent,
  TriggerHealthStats,
  TriggerSummary,
} from '../types';
import { ApiHttpError, apiFetch, apiBaseUrl } from '../http';
import { canonicalJsonStringify } from '../mock/sign';

// ── Public GETs ───────────────────────────────────────────────────────────────

export async function listTriggers(): Promise<TriggerSummary[]> {
  return apiFetch<TriggerSummary[]>('/triggers/list');
}

export async function getTriggerHealth(): Promise<TriggerHealthStats> {
  return apiFetch<TriggerHealthStats>('/triggers/health');
}

export async function getTriggerRecentEvents(
  id: string,
): Promise<TriggerFireEvent[]> {
  return apiFetch<TriggerFireEvent[]>(`/triggers/${id}/recent`);
}

// ── Cron validator ────────────────────────────────────────────────────────────

export async function validateCronExpression(
  expression: string,
): Promise<CronValidateResponse> {
  return founderFetch<CronValidateResponse>('/admin/cron-validate', {
    expression,
  });
}

// ── Founder-gated mutations ───────────────────────────────────────────────────

export async function createTrigger(
  req: CreateTriggerRequest,
  founderSig: string,
): Promise<CreateTriggerResponse> {
  return founderFetch<CreateTriggerResponse>(
    '/admin/trigger-create',
    req,
    founderSig,
  );
}

export async function setTriggerEnabled(
  id: string,
  req: SetTriggerEnabledRequest,
  enabled: boolean,
  founderSig: string,
): Promise<SetTriggerEnabledResponse> {
  const path = enabled
    ? `/admin/trigger-enable/${id}`
    : `/admin/trigger-disable/${id}`;
  return founderFetch<SetTriggerEnabledResponse>(path, req, founderSig);
}

// ── Webhook fire (regular auth) ───────────────────────────────────────────────

export async function fireTrigger(id: string): Promise<FireTriggerResponse> {
  return apiFetch<FireTriggerResponse>(`/triggers/${id}/fire`, {
    method: 'POST',
    body: {},
  });
}

// ── Internal helpers ──────────────────────────────────────────────────────────

async function founderFetch<T>(
  path: string,
  body: unknown,
  founderSig?: string,
): Promise<T> {
  const bodyStr = canonicalJsonStringify(body);
  const url = `${apiBaseUrl()}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json, application/problem+json',
  };
  if (founderSig) {
    headers['X-Founder-Signature'] = founderSig;
  }
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: bodyStr,
  });

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  if (!response.ok) {
    const ct = response.headers.get('content-type') ?? '';
    if (ct.includes('application/json') || ct.includes('problem+json')) {
      try {
        const parsed = JSON.parse(raw) as {
          type?: string;
          title?: string;
          detail?: string;
        };
        const slug =
          parsed.type?.split(/[/#]/).filter(Boolean).pop()?.toLowerCase() ??
          String(response.status);
        throw new ApiHttpError({
          status: response.status,
          code: slug,
          title: parsed.title ?? String(response.status),
          detail: parsed.detail,
        });
      } catch (e) {
        if (e instanceof ApiHttpError) throw e;
      }
    }
    throw new ApiHttpError({
      status: response.status,
      code: String(response.status),
      title: raw.length > 0 && raw.length < 256 ? raw : `HTTP ${response.status}`,
    });
  }

  if (!raw) return undefined as T;
  return JSON.parse(raw) as T;
}
